<template>
  <Grid
    class="certificates-grid"
    :pageable="pageable"
    :data-items="products"
    :skip="skip"
    :take="take"
    :page-size="take"
    :total="totalProducts"
    :columns="columns"
    :loader="productsLoading"
    :filter="filter"
    :column-menu="!productsLoading"
    :selected-field="selectedField"
    :sort="sort"
    :resizable="true"
    :reorderable="true"
    @columnresize="onColumnResize"
    @columnreorder="onColumnReorder"
    @filterchange="filterChangeHandler"
    @pagechange="pageChangeHandler"
    @selectionchange="changeProductSelection"
    @sortchange="sortChangeHandler"
    @rowclick="changeProductSelection"
    data-cy="certificates-grid"
  >
    <template v-slot:columnMenu="{ props }">
      <ColumnMenu
        :column="props.column"
        :filterable="props.filterable"
        :filter="props.filter"
        :sortable="props.sortable"
        :sort="props.sort"
        :columns="columns"
        :getItems="getPropValues"
        @sortchange="(e) => props.onSortchange(e)"
        @filterchange="(e) => props.onFilterchange(e)"
        @closemenu="(e) => props.onClosemenu(e)"
        @contentfocus="(e) => props.onContentfocus(e)"
        @columnssubmit="onColumnsSubmit"
      />
    </template>
    <template v-slot:SelectHeaderCell>
      <th>
        <input
          type="checkbox"
          :checked="allPageProductsSelected"
          :disabled="showSelectionPopup"
          ref="selectAllCheckbox"
          @change="onHeaderSelectionChange"
          data-cy="select-all-checkbox"
        />
        <Popup
          class="actions-popup"
          anchor="selectAllCheckbox"
          :show="showSelectionPopup"
          popup-class="popup-content"
          :anchor-align="{ horizontal: 'right', vertical: 'middle' }"
          :popup-align="{ horizontal: 'left', vertical: 'middle' }"
        >
          <div style="height: 60px" class="content popup-inner-content">
            <p>
              {{ selectionHeaderChecked ? "Select" : "Deselect" }} all
              {{ totalProducts }} items or just the {{ products.length }} items
              on this page?
            </p>
            <Button
              v-for="action in popupActions"
              :key="action.text"
              :theme-color="action.theme"
              @click="action.onClick"
              data-cy="select-all-popup-button"
            >
              {{ action.text }}
            </Button>
          </div>
        </Popup>
      </th>
    </template>
    <template v-slot:GroupCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        {{ convertNumber(dataItem[field]) }}
      </td>
    </template>
    <template v-slot:UkdCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        {{ dataItem.LocSelected !== "Suppress UKD#" ? dataItem[field] : "" }}
      </td>
    </template>
    <template v-slot:PesachCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        {{ dataItem[field] ? "Y" : "N" }}
      </td>
    </template>
    <template v-slot:ConfidentialCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        {{ dataItem[field] !== EnumConfidential.NotConfidential ? "Y" : "N" }}
      </td>
    </template>
  </Grid>
</template>

<script setup lang="ts">
import type { ProductDisplay } from "@/Domain/ProductDisplay";
import {
  Grid,
  type GridColumnProps,
  type GridColumnReorderEvent,
  type GridColumnResizeEvent,
  type GridFilterChangeEvent,
  type GridPageChangeEvent,
  type GridPagerSettings,
  type GridRowClickEvent,
  type GridSelectionChangeEvent,
} from "@progress/kendo-vue-grid";
import { ref, computed, watchEffect } from "vue";
import type {
  CompositeFilterDescriptor,
  SortDescriptor,
} from "@progress/kendo-data-query";
import type { ProductWithSelection } from "@/api/kosherCertificates";
import { Popup } from "@progress/kendo-vue-popup";
import { Button } from "@progress/kendo-vue-buttons";
import ColumnMenu from "@/shared/ui/inputs/ColumnMenu.vue";
import type {
  ItemChangedOptions,
  ItemSortChangedOptions,
} from "@/Domain/ItemChangedOptions";
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";
import {
  getFilterOperatorsForBoolean,
  getFilterOperatorsForNumber,
} from "@/shared/helpers/filtersHelper";
import { EnumConfidential } from "@/Domain/EnumConfidential";
import { convertNumber } from "@/Domain/EnumGroupConverter";
import { GridOptionsService } from "@/shared/services/GridOptionsService";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { GridMenuActionType } from "@/Domain/GridMenuActionType";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";

const notificationStore = useNotificationStore();

const props = defineProps<{
  products: ProductWithSelection[];
  productsLoading: boolean;
  productsSkip: number;
  pageSize: number;
  totalProducts: number;
  filter: CompositeFilterDescriptor;
  sort: SortDescriptor[];
  getPropValues: (
    column: GridColumnPropsExtended
  ) => Promise<ProductWithSelection[]>;
}>();

const emit = defineEmits<{
  (e: "productDetailsClicked", product: ProductDisplay): void;
  (e: "productsChanged", options: ItemChangedOptions): void;
  (e: "selectionChanged", selectedProducts: ProductWithSelection[]): void;
  (e: "selectAllClicked", checked: boolean): void;
  (e: "sortChanged", options: ItemSortChangedOptions): void;
  (e: "columnreorder", event: GridColumnReorderEvent): void;
  (e: "columnssubmit", columnsProps: GridColumnProps[]): void;
}>();

const gridName = "certificatesGrid";

const skip = ref<number>(props.productsSkip);
const take = ref<number>(props.pageSize);
const showSelectionPopup = ref<boolean>(false);
const selectionHeaderChecked = ref<boolean>(false);

const selectedField = "selected";
const columns = ref<GridColumnPropsExtended[]>([
  {
    title: "Details",
    field: "selected",
    width: "50px",
    sortable: false,
    filterable: false,
    headerCell: "SelectHeaderCell",
    headerClassName: "hide-menu",
    hideInColumnMenu: true,
  },
  {
    title: "Label Number",
    field: "LabelNumber",
    columnMenu: "columnMenu",
  },
  {
    title: "Label Name",
    field: "LabelName",
    columnMenu: "columnMenu",
    hideInColumnMenu: true,
  },
  {
    title: "Label Company",
    field: "DistributorCompanyName",
    columnMenu: "columnMenu",
  },
  {
    title: "Brand Name",
    field: "BrandName",
    columnMenu: "columnMenu",
  },
  {
    title: "Symbol",
    field: "Symbol",
    columnMenu: "columnMenu",
    width: "80px",
  },
  {
    title: "Group",
    field: "_Group",
    cell: "GroupCell",
    columnMenu: "columnMenu",
    width: "80px",
    allowedFilterOperators: getFilterOperatorsForNumber(),
  },
  {
    title: "Consumer Industrial",
    field: "_ConsumerIndustrial",
    columnMenu: "columnMenu",
  },
  {
    title: "UKD ID",
    field: "VueFullUkdIdWithSuppressUkdId",
    cell: "UkdCell",
    columnMenu: "columnMenu",
  },
  {
    title: "Pesach",
    field: "_Pesach",
    cell: "PesachCell",
    columnMenu: "columnMenu",
    allowedFilterOperators: getFilterOperatorsForBoolean(),
    width: "80px",
  },
  {
    title: "Confidential",
    field: "_Confidential",
    cell: "ConfidentialCell",
    columnMenu: "columnMenu",
    allowedFilterOperators: getFilterOperatorsForBoolean(),
  },
]);

const popupActions = [
  {
    text: "All",
    theme: "success",
    onClick: () => {
      props.products.forEach(
        (product) => (product.selected = selectionHeaderChecked.value)
      );
      emit("selectAllClicked", selectionHeaderChecked.value);
      showSelectionPopup.value = false;
    },
  },
  {
    text: "Page",
    theme: "warning",
    onClick: () => {
      props.products.forEach(
        (product) => (product.selected = selectionHeaderChecked.value)
      );
      emit("selectionChanged", props.products);
      showSelectionPopup.value = false;
    },
  },
];

let pageSizes = [10, 20, 50, 100];
if (!pageSizes.includes(take.value)) pageSizes.push(take.value);
pageSizes = pageSizes.sort((a, b) => a - b);
const pageable = ref<GridPagerSettings>({
  buttonCount: 5,
  info: true,
  type: "numeric",
  pageSizes: pageSizes,
  previousNext: true,
});
const allPageProductsSelected = computed(() => {
  return props.products.every((product) => product.selected);
});

GridOptionsService.loadGridOptions(gridName, columns.value);

const filterChangeHandler = function (e: GridFilterChangeEvent) {
  skip.value = 0;
  emit("productsChanged", { skip: 0, take: take.value, filterEvent: e });
};

const sortChangeHandler = function (e: any) {
  const descriptors = e.sort as SortDescriptor[];
  if (!descriptors) return;
  skip.value = 0;
  emit("sortChanged", {
    field: descriptors[0].field,
    ascending: descriptors[0].dir === "asc",
  });
};

const pageChangeHandler = function (event: GridPageChangeEvent) {
  skip.value = event.page.skip;
  take.value = event.page.take;
  emit("productsChanged", {
    skip: event.page.skip,
    take: event.page.take,
    filterEvent: null,
  });
};

const onHeaderSelectionChange = function (event: Event) {
  showSelectionPopup.value = true;
  selectionHeaderChecked.value = (event.target as HTMLInputElement).checked;
};

const hideSelectionPopup = function () {
  showSelectionPopup.value = false;
};

const changeProductSelection = function (
  event: GridSelectionChangeEvent | GridRowClickEvent
) {
  const product = event.dataItem as ProductWithSelection;
  if (!product) return;
  product.selected = !product.selected;
  emit("selectionChanged", [product]);
};

const onColumnsSubmit = function (columnsProps: GridColumnProps[]) {
  if (columnsProps) {
    columns.value = columnsProps;
    GridOptionsService.saveVisibleColumns(gridName, columnsProps);
  }
  console.debug("KosherCertificatesGrid.vue, onColumnSubmit", columnsProps);

  emit("columnssubmit", columnsProps);
};

const onColumnResize = function (e: GridColumnResizeEvent) {
  const col = GridOptionsService.getResizedColumn(e, columns.value);
  if (col.title)
    GridOptionsService.saveColumnWidth(gridName, col.title, e.newWidth);
};

const onColumnReorder = function (e: GridColumnReorderEvent) {
  GridOptionsService.setSelectDetailsColumnOrder(e);
  columns.value = e.columns;
  GridOptionsService.saveColumnsOrder(gridName, e.columns);
  emit("columnreorder", e);
};

const resetColumnSelection = function () {
  columns.value.forEach((col) => {
    col.hidden = col.defaultHiddenValue ?? false;
  });
  GridOptionsService.clearVisibleColumns(gridName);
};

const resetColumnSizes = function (showNotification = true) {
  GridOptionsService.clearColumnWidths(gridName);
  if (showNotification) {
    notificationStore.notify({
      type: NotificationTypeEnum.success,
      message:
        "Column sizes reset successfully. Please refresh the page to see the changes.",
      duration: 5000,
    });
  }
};

const resetColumnOrder = function () {
  columns.value.forEach((col) => {
    col.orderIndex = undefined;
  });
  GridOptionsService.clearColumnsOrder(gridName);
};

const resetAllSettings = function () {
  resetColumnSelection();
  resetColumnSizes(false);
  resetColumnOrder();

  notificationStore.notify({
    type: NotificationTypeEnum.success,
    message:
      "All settings reset successfully. Please refresh the page to see the changes.",
    duration: 5000,
  });
};

const executeAction = function (actionType: GridMenuActionType) {
  switch (actionType) {
    case GridMenuActionType.ResetColumnSelection:
      resetColumnSelection();
      break;
    case GridMenuActionType.ResetColumnSizes:
      resetColumnSizes();
      break;
    case GridMenuActionType.ResetColumnOrder:
      resetColumnOrder();
      break;
    case GridMenuActionType.ResetAllSettings:
      resetAllSettings();
      break;
  }
};

defineExpose({
  hideSelectionPopup,
  executeAction,
});

watchEffect(() => {
  skip.value = props.productsSkip;
});

watchEffect(() => {
  take.value = props.pageSize;
});
</script>

<style scoped lang="scss">
.certificates-grid {
  min-height: 450px;
}
.k-grid {
  a.is-download-link {
    svg {
      height: 12px;
    }
    &:before {
      content: "";
    }
  }
}
.actions-popup {
  .content {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
  }

  &:after {
    content: "";
    position: absolute;
    border: 10px solid transparent;
    bottom: 17px;
    left: -20px;
    border-right-color: #f9f9f9;
  }
}
</style>
